import { Button, Link, Paper, Stack, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useId, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Alert, FormField, Text } from '@notablehealth/design-system';
import { type api } from 'common';

import { ReactComponent as NotableSvg } from '../../assets/legacy/notable.svg';
import passwordEye from '../../assets/legacy/password-icons/eye.png';
import { getLoginEmail } from '../../utils/login';

import styles from './index.module.css';

interface Props {
  loading: boolean;
  handleSso: (endpoint: string) => void;
  getUserDetails: (email: string) => Promise<api.User.UserDetails | undefined>;
  handleLogin: (email: string, password: string) => Promise<void>;
}

enum Step {
  Email = 'email',
  Password = 'password',
}

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function LoginForm(props: Props) {
  const { loading, handleSso, handleLogin, getUserDetails } = props;
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState<string>(getLoginEmail() ?? '');
  const [password, setPassword] = useState('');
  const [revealPassword, setRevealPassword] = useState(false);
  const [step, setStep] = useState(Step.Email);

  const userDetailsMutation = useMutation({
    mutationFn: getUserDetails,
    onSuccess: (details) => {
      if (details?.ssoEnabled) {
        handleSso(details.ssoEndpoint);
      } else {
        setStep(Step.Password);
      }
    },
  });

  const handleEmailChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      setStep(Step.Email);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
      setEmail(String(event.currentTarget.value).toLowerCase());
    },
    []
  );

  return (
    <Paper
      elevation={0}
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 4,
        maxWidth: '437px',
        paddingY: 6,
        paddingX: 5,
      }}
    >
      <Stack flex={1} alignItems="center" gap={3}>
        <div className={styles.NotableLogo}>
          <NotableLogo />
        </div>
        <Text variant="headline-small" bold className={styles.FormHeader}>
          Log in to your account
        </Text>
      </Stack>
      <div className={styles.Inputs}>
        <Stack>
          <FormField id={emailId} label="Work email">
            <TextField
              id={emailId}
              name="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
            />
          </FormField>
        </Stack>
        {step === Step.Password && userDetailsMutation.data?.ssoEnabled === false && (
          <Stack className={styles.PasswordGroup}>
            <FormField id={passwordId} label="Password">
              <TextField
                id={passwordId}
                name="password"
                type={revealPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
                disabled={loading}
                autoFocus
              />
            </FormField>
            {password.length > 0 && (
              <span
                className={styles.PasswordEye}
                onClick={() => setRevealPassword(!revealPassword)}
              >
                <img src={passwordEye} alt="password show hide" />
              </span>
            )}
          </Stack>
        )}
      </div>
      <Button
        variant="contained"
        type="submit"
        disabled={!emailRegex.test(email) || (step === Step.Password && password.length === 0)}
        loading={userDetailsMutation.isPending || loading}
        onClick={async (event) => {
          event.preventDefault();
          if (step === Step.Email) {
            userDetailsMutation.mutate(email);
            return;
          }
          await handleLogin(email, password);
        }}
        fullWidth
      >
        Continue
      </Button>
      {userDetailsMutation.isError && (
        <Alert variant="danger" className="mt-4">
          Error checking email, please try again.
        </Alert>
      )}
      <div className={styles.TermsOfService}>
        <span>By signing in, you agree to our </span>
        <Link to="/terms-of-use" component={ReactRouterLink}>
          Terms of Use
        </Link>
        .
      </div>
    </Paper>
  );
}

const NotableLogo = styled(NotableSvg)`
  width: 147px;
  height: 21px;
`;
